import { forwardRef, useRef } from 'react'

import { QuestionCircleLineIcon } from '@inno-ui/icons'
import cn from 'classnames'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AppGroup as AppGroupType } from 'shared/constants/apps'
import { useCanScroll, useTutorialButton } from 'shared/hooks'
import {
  CLUSTERIX_LOGO_TOUR_ID,
  TOP_DECK_TOUR_ID,
  USER_MENU_TOUR_ID
} from 'shared/hooks/useOnboarding'
import { HotkeyTooltip } from 'src/shared/components/HotkeyTooltip/HotkeyTooltip'

import { InnoscriptaContacts } from '../../InnoscriptaContacts'
import { CommonDockProps } from '../../LaunchPad/LaunchPad.types'
import { TutorialModal } from '../../TutorialModal/TutorialModal'
import { UserProfile } from '../../UserProfile'
import { ClusterixLogo } from '../components/ClusterixLogo'
import {
  useTopDockActionButtons,
  useTopDockApps,
  useTopDockAutoScroll
} from '../hooks'
import styles from './TopDock.module.scss'
import { ActionButton, AppGroup, NewsTickerBanner } from './components'
import { useSortableApps } from './hooks/useSortableApps'

const MotionLink = motion.create(Link)

const TopDockDesktop = forwardRef<HTMLDivElement, CommonDockProps>(
  ({ handleLogoClick, handleAppClick, iconRefs }, ref) => {
    const { t } = useTranslation('translation')
    const scrollableContentRef = useRef<HTMLDivElement>(null)
    const { appsByGroup, appIndexMap, isLoading } = useTopDockApps()
    const actionButtons = useTopDockActionButtons()
    const {
      configs: tutorialConfigs,
      isOpen: isTutorialOpen,
      open: openTutorial,
      close: closeTutorial
    } = useTutorialButton()
    const { scrollButtonIntoView } = useTopDockAutoScroll(scrollableContentRef)
    const { left: canScrollLeft, right: canScrollRight } =
      useCanScroll(scrollableContentRef)
    const { sensors, handleDragEnd } = useSortableApps({ appsByGroup })

    return (
      <motion.header
        ref={ref}
        initial={{
          y: -50,
          opacity: 0
        }}
        animate={{
          y: 0,
          opacity: 1
        }}
        exit={{
          y: -100,
          opacity: 0
        }}
        transition={{
          type: 'spring',
          stiffness: 700,
          damping: 40,
          delay: 0.4
        }}
      >
        <NewsTickerBanner />
        <div className={styles.topDock}>
          <div className={styles.leftSection}>
            <MotionLink
              data-tour={CLUSTERIX_LOGO_TOUR_ID}
              to='/'
              onClick={handleLogoClick}
              className={styles.logo}
              aria-label={t('navbar.go_to_home')}
              whileHover={{
                scale: 1.05
              }}
            >
              <motion.span className={styles.logoIcon}>
                <ClusterixLogo />
              </motion.span>
            </MotionLink>

            <div className={styles.groupWrapper}>
              {actionButtons.map(
                (
                  {
                    Icon,
                    onClick,
                    ariaLabel,
                    isActive,
                    tourId,
                    shortcut,
                    unreadCount
                  },
                  index
                ) => (
                  <HotkeyTooltip
                    key={`action-button-${index}`}
                    keys={shortcut}
                    onPress={onClick}
                    label={ariaLabel}
                    previewHotkeysOnPress={false}
                  >
                    <ActionButton
                      index={index}
                      Icon={Icon}
                      onClick={onClick}
                      ariaLabel={ariaLabel}
                      isActive={isActive}
                      tourId={tourId}
                      unreadCount={unreadCount}
                    />
                  </HotkeyTooltip>
                )
              )}
            </div>
          </div>

          <div
            className={cn(styles.centerSection, {
              [styles.canScrollLeft]: canScrollLeft,
              [styles.canScrollRight]: canScrollRight
            })}
          >
            <motion.div
              ref={scrollableContentRef}
              className={styles.content}
              data-tour={TOP_DECK_TOUR_ID}
            >
              {appsByGroup.map((appGroup, groupIndex) => {
                const groupKey = Object.keys(appGroup)[0] as AppGroupType
                const groupApps = Object.values(appGroup)?.[0] || []

                return (
                  <AppGroup
                    key={`appgroup-${groupIndex}`}
                    group={groupKey}
                    apps={groupApps}
                    appIndexMap={appIndexMap}
                    sensors={sensors}
                    onDragEnd={handleDragEnd}
                    handleAppClick={handleAppClick}
                    iconRefs={iconRefs}
                    scrollButtonIntoView={scrollButtonIntoView}
                    isLoading={isLoading}
                  />
                )
              })}
            </motion.div>
          </div>

          <div className={styles.rightSection} data-tour={USER_MENU_TOUR_ID}>
            {tutorialConfigs.length > 0 && (
              <div className={styles.helpButton}>
                <HotkeyTooltip
                  onPress={openTutorial}
                  label={t('tutorial_modal.tooltip')}
                  previewHotkeysOnPress={false}
                >
                  <ActionButton
                    index={0}
                    Icon={QuestionCircleLineIcon}
                    onClick={openTutorial}
                    ariaLabel={t('desktop.shortcut_item.need_help')}
                  />
                </HotkeyTooltip>
              </div>
            )}
            <InnoscriptaContacts />
            <UserProfile />
          </div>
        </div>
        {tutorialConfigs.length > 0 && (
          <TutorialModal
            isOpen={isTutorialOpen}
            onClose={closeTutorial}
            configs={tutorialConfigs}
          />
        )}
      </motion.header>
    )
  }
)

export { TopDockDesktop }
